import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AboutGraphic from '../assets/images/About.jpeg'; 
import AboutTransformation from '../assets/images/About2.webp'; 

function About() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-12');
            entry.target.classList.add('opacity-100', 'translate-y-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const hiddenElements = document.querySelectorAll('.animate-on-scroll');
    hiddenElements.forEach((el) => observer.observe(el));

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-20 px-6">
      <div className="max-w-6xl mx-auto">
        
        <div className="relative overflow-hidden rounded-2xl bg-slate-900/60 backdrop-blur-md border border-slate-800 p-10 md:p-14 mb-8 shadow-lg animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out">
          
          <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl translate-x-1/4 -translate-y-1/3 pointer-events-none"></div>

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
            
            <div className="md:w-2/5 w-full flex-shrink-0 relative group">
              {/* Outer glow effect on hover */}
              <div className="absolute inset-0 bg-blue-500/20 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              <div className="w-full aspect-square md:aspect-[4/3] rounded-2xl bg-slate-800/80 border border-slate-700 overflow-hidden shadow-2xl relative z-10">
                <img 
                  src={AboutGraphic} 
                  alt="iLAMI Technologies Leadership Team" 
                  className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
                />
              </div>
            </div>

            <div className="md:w-3/5">
              <p className="text-blue-500 text-xs uppercase tracking-[0.3em] font-bold mb-3">Meet the Visionaries</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-5 tracking-tight">Our Architectural Leadership</h2>
              <div className="h-1 w-16 bg-blue-500 mb-6"></div>
              <p className="text-slate-300 text-base leading-relaxed font-medium mb-4">
                At iLAMI Technologies, transformation isn't an abstract concept; it's engineered execution led by industry veterans. Our senior architects and data engineers possess deep expertise bridging the enterprise and public sectors.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed">
                We cultivate a culture of rigorous technical analysis and scalable design. We don't just provision infrastructure; we architect data structures and operational frameworks designed for security, strict compliance, and long-term resilient digital ecosystems.
              </p>
            </div>
          </div>
        </div>

        <div 
          className="relative overflow-hidden rounded-2xl bg-slate-900 border border-slate-800 p-10 md:p-16 mb-12 shadow-lg animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out"
          style={{ transitionDelay: '200ms' }}
        >
          <div className="absolute inset-0 z-0">
            <img 
              src={AboutTransformation} 
              className="w-full h-full object-cover object-right opacity-40 mix-blend-screen" 
              alt="Digital Transformation Background" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-transparent"></div>
          </div>
          
          <div className="relative z-10 md:w-2/3">
            <p className="text-blue-500 text-xs uppercase tracking-[0.3em] font-bold mb-3">Discover Our Foundation</p>
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-6 leading-tight">
              Driving Digital <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">Transformation.</span>
            </h1>
            <p className="text-slate-300 text-lg font-medium leading-relaxed max-w-xl">
              iLAMI Technologies bridges the gap between complex organizational challenges and highly secure, scalable IT infrastructure. We engineer solutions built for the demands of the modern enterprise and the public sector.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          
          <div 
            className="bg-slate-900/60 backdrop-blur-md border border-slate-800 p-10 rounded-2xl hover:border-blue-500/30 transition-colors duration-500 group animate-on-scroll opacity-0 translate-y-12"
            style={{ transitionDelay: '350ms' }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-blue-900/30 rounded-lg text-blue-500 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
              </div>
              <h3 className="text-2xl font-bold text-white tracking-tight">Our Vision</h3>
            </div>
            <p className="text-slate-400 text-base leading-relaxed">
              To be the leading force in technological innovation, transforming businesses globally by architecting secure, resilient digital ecosystems that stand the test of time.
            </p>
          </div>

          <div 
            className="bg-slate-900/60 backdrop-blur-md border border-slate-800 p-10 rounded-2xl hover:border-blue-500/30 transition-colors duration-500 group animate-on-scroll opacity-0 translate-y-12"
            style={{ transitionDelay: '450ms' }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-blue-900/30 rounded-lg text-blue-500 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
              </div>
              <h3 className="text-2xl font-bold text-white tracking-tight">Our Mission</h3>
            </div>
            <p className="text-slate-400 text-base leading-relaxed">
              To deliver exceptional, framework-aligned IT solutions that drive operational efficiency, ensure strict data compliance, and foster sustainable growth for our clients.
            </p>
          </div>

        </div>

        <div 
          className="bg-slate-900/60 backdrop-blur-md border border-slate-800 p-10 md:p-12 rounded-2xl shadow-lg relative overflow-hidden animate-on-scroll opacity-0 translate-y-12"
          style={{ transitionDelay: '550ms' }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-3xl font-extrabold text-white mb-4 tracking-tight">What We Do Best</h3>
              <div className="h-1 w-16 bg-blue-500 mb-6"></div>
              <p className="text-slate-400 text-base leading-relaxed">
                We excel in translating complex public sector and enterprise challenges into streamlined, highly secure technical solutions. Our expertise spans custom software development, cloud integration, robust cybersecurity, and comprehensive enterprise architecture. We ensure your operations remain secure, compliant, and always ahead of the curve.
              </p>
            </div>

            <div>
              <h3 className="text-3xl font-extrabold text-white mb-4 tracking-tight">Commitment to Innovation</h3>
              <div className="h-1 w-16 bg-blue-500 mb-6"></div>
              <p className="text-slate-400 text-base leading-relaxed mb-8">
                We are continuously evolving and adopting the latest technological advancements to provide top-tier, scalable solutions. Whether developing for modern enterprises or managing data structures for municipalities, our commitment to excellence is unwavering.
              </p>
              
              <Link to="/portfolio" className="inline-flex items-center gap-2 text-blue-400 font-bold hover:text-blue-300 transition-colors uppercase tracking-widest text-xs group">
                View Our Portfolio 
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default About;